import { Box, Modal, Avatar } from "@mui/material";
import { useTranslation } from "react-i18next";
import { EmployeeUser } from "../../../types";
import { ScheduleEmployee } from "../schedule-employee";
import { DownloadPDF } from "../../DownloadPDF";
import profileGenericPic from "../../../assets/img/logos/profileGenericPic.jpg";
import { BranchOrigin } from "../../../enums/branch";
import { getBranchDisplayName } from "../../../utils/branch-display-name";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "60%",
  maxWidth: 800,
  maxHeight: "80vh",
  overflowY: "auto",
  bgcolor: "#fff",
  border: "1px solid #898989",
  boxShadow: 24,
  borderRadius: 4,
};

interface EmployeeDetailsARProps {
  employee: EmployeeUser;
  isOpen: boolean;
  onClose: () => void;
}

export function EmployeeDetailsAR({ employee, isOpen, onClose }: EmployeeDetailsARProps) {
  const { t } = useTranslation();

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby={t("employee.info.label")}
      aria-describedby={t("employee.info.label")}
    >
      <Box sx={style}>
        <div className="bg-gray-light text-center text-lg font-normal text-black py-6 px-2">
          <h1 className="text-lg font-normal">{t("employee.info.title")}</h1>
        </div>
        <div className="p-6">
          <div className="flex flex-col gap-6">
            {/* Información básica del empleado */}
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Avatar
                    sx={{
                      width: "4rem",
                      height: "4rem",
                    }}
                    src={employee.imageUrl ?? profileGenericPic}
                    alt="Avatar Usuario"
                  />
                  <div>
                    <h2 className="text-xl font-montserrat font-medium text-gray-800">
                      {employee.name}
                    </h2>
                    <p className="text-sm text-gray-500">{employee.email}</p>
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs mt-1 ${
                      employee.isVerified 
                        ? "border-green text-green" 
                        : "border-orange text-orange"
                    }`}>
                      {employee.isVerified ? t("validated") : t("pending-validation")}
                    </span>
                  </div>
                </div>
                <DownloadPDF
                  userId={employee.userId}
                  employeeName={employee.name}
                  variant="mui"
                  branchId={employee.branchId}
                  disabled={employee.branchOrigin === BranchOrigin.DIVIX && (!employee.isVerified || !employee.qrTipsCode)}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Sucursal */}
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">
                    {t("employee.info.branch")}
                  </span>
                  <span className="text-base text-gray-800 border rounded-full px-3 py-1 w-fit">
                    {getBranchDisplayName({
                      fantasyName: employee.fantasyName,
                      name: employee.branchName,
                    })}
                  </span>
                </div>

                {/* Grupos del empleado */}
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">
                    {t("employee.info.groups")}
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {employee.userGroups && employee.userGroups.length > 0 ? (
                      employee.userGroups.map((group, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center rounded-full border px-3 py-1 text-sm"
                        >
                          {group.name}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-gray-500">
                        {t("employee.info.no-groups")}
                      </span>
                    )}
                  </div>
                </div>

                {/* Fecha de nacimiento */}
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">
                    {t("employee.info.birthday")}
                  </span>
                  <span className="text-base text-gray-800 border rounded-full px-3 py-1 w-fit">
                    {employee.birthDate ? new Date(employee.birthDate).toLocaleDateString() : "-"}
                  </span>
                </div>

                  {/* DNI */}
                  <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">
                    {t("employee.info.dni")}
                  </span>
                  <span className="text-base text-gray-800 border rounded-full px-3 py-1 w-fit">
                    {employee.dni || "-"}
                  </span>
                </div>

                 {/* CUIT/CUIL */}
                 <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">
                    {t("employee.info.identityNumber")}
                  </span>
                  <span className="text-base text-gray-800 border rounded-full px-3 py-1 w-fit">
                    {employee.identityNumber || "-"}
                  </span>
                </div>

                {/* Dirección */}
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">
                    {t("employee.info.address")}
                  </span>
                  <span className="text-base text-gray-800 border rounded-full px-3 py-1 w-fit">
                    {employee.address || "-"}
                  </span>
                </div>

                {/* Teléfono */}
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">
                    {t("employee.info.phone")}
                  </span>
                  <span className="text-base text-gray-800 border rounded-full px-3 py-1 w-fit">
                    {employee.phone || "-"}
                  </span>
                </div>

                {/* CBU/CVU/Alias */}
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">
                    {t("employee.info.bankAccountNumber")}
                  </span>
                  <span className="text-base text-gray-800 border rounded-full px-3 py-1 w-fit">
                    {employee.bankAccountNumber || "-"}
                  </span>
                </div>

                {/* Uso de QR */}
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">
                    {t("employee.info.qr")}
                  </span>
                  <span className="text-base text-gray-800 border rounded-full px-3 py-1 w-fit">
                    {employee.useQR ? t("employee.info.qr-enabled") : t("employee.info.qr-disabled")}
                  </span>
                </div>
              </div>
            </div>

            {/* Separador */}
            <div className="border-t border-gray-200"></div>

            {/* Horario del empleado */}
            <ScheduleEmployee employee={employee} />
          </div>
        </div>
      </Box>
    </Modal>
  );
}

